/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQuery } from '@tanstack/react-query';

import { ApiError, GetAvailablePermissionsResponse, Permission } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export interface PermissionGroup {
  resource: string;
  displayName: string;
  permissions: Permission[];
}

/**
 * Capitalizes resource names by splitting on hyphens and capitalizing each word
 */
function capitalizeResource(resource: string): string {
  return resource
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Groups permissions by resource type and sorts them consistently
 */
function groupPermissions(permissions: Permission[]): PermissionGroup[] {
  const groups = permissions.reduce(
    (acc, permission) => {
      const { resource } = permission;
      if (!acc[resource]) {
        acc[resource] = {
          resource,
          displayName: capitalizeResource(resource),
          permissions: [],
        };
      }
      acc[resource].permissions.push(permission);
      return acc;
    },
    {} as Record<string, PermissionGroup>,
  );

  // Sort groups by display name and permissions within each group by action
  return Object.values(groups)
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((group) => ({
      ...group,
      permissions: group.permissions.sort((a, b) => a.action.localeCompare(b.action)),
    }));
}

/**
 * Custom hook that fetches available permissions for API tokens and groups them by resource
 *
 * @returns React Query result containing:
 *   - data: Array of grouped permissions
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *
 * @example
 * ```tsx
 * const { data: permissionGroups, isLoading } = useAvailablePermissions();
 * ```
 */
export function useAvailablePermissions() {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return useQuery<PermissionGroup[], ApiError>({
    queryKey: ['availablePermissions', selectedTenantId],
    queryFn: async (): Promise<PermissionGroup[]> => {
      const response: GetAvailablePermissionsResponse = await mgmtPlaneAPIClient.apiTokens.getAvailablePermissions({
        tenantId: selectedTenantId!,
      });

      return groupPermissions(response.data);
    },
    enabled: !!selectedTenantId,
  });
}
